/**
 * RoyaleSpectateView (web spectate)
 *
 * Read-only board for a SPECTATOR following one player live. The "Watching X"
 * stats live in the combined RoyaleStrip above (PuzzleRoyaleBattle); this is
 * just the board.
 *
 * Live moves: the playing client streams EVERY ply to spectators as
 * `fun_spectate_move {playerId, fen, from, to, orientation, kind}`; the store
 * records the latest per player in `liveBoards` (with a monotonic `seq`). This
 * component renders the followed player's streamed FEN so react-chessboard
 * animates the pieces sliding move-by-move.
 *
 * Two refinements over the naive "render latest FEN":
 *   - ORIENTATION LOCK (#2): we use the streamed `orientation` (the followed
 *     player's side, fixed per puzzle) — NOT the FEN's side-to-move — so the
 *     board never flips between a player ply and the auto-reply.
 *   - SETTLE-ON-COMPLETION (#3): when a `kind:'setup'` ply arrives (a NEW
 *     puzzle loaded), we DON'T apply it immediately — we hold the previous
 *     puzzle's final position for SETTLE_MS so the spectator sees what the
 *     player solved, then swap to the new puzzle's setup.
 */

import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {Chess} from 'chess.js';

import CCText from '@/Atoms/CCText';
import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';
import {FontSizes} from '@/Utils/Dimensions';

import type {BattlePuzzle} from '@/Screens/FunChallenges/types';
import type {PlayerRoyaleState} from '@/Screens/FunChallenges/royaleTypes';

const TEAL_DARK = '#1F6C63';
const TEXT_MUTED = '#8A7E5F';
const STARTING_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/** Hold a completed puzzle's final position this long before the next puzzle's
 *  setup animates in, so the spectator sees the solved position (#3). */
const SETTLE_MS = 1500;

interface LiveBoard {
  fen: string;
  from: string;
  to: string;
  orientation: 'white' | 'black';
  // 'wrong' = the followed player's mistake; applied immediately like a player
  // ply, then held by the next 'setup' so the spectator SEES it (#4).
  kind: 'setup' | 'player' | 'reply' | 'wrong';
  seq: number;
}

interface Props {
  /** The puzzle id sequence (session.coords). */
  coords: string[];
  /** Hydrated puzzle rows. */
  puzzles: Record<string, BattlePuzzle>;
  /** The followed player's live state (null → none selected yet). */
  followed: PlayerRoyaleState | null;
  /** The followed player's latest streamed ply (live board), if any. */
  liveBoard: LiveBoard | null;
  boardWidth: number;
}

interface Shown {
  fen: string;
  orientation: 'white' | 'black';
}

/** The followed player's CURRENT puzzle start (setup ply applied), oriented to
 *  the player's side. Fallback before any ply streams in. */
function currentPuzzleBoard(puzzle: BattlePuzzle | undefined): Shown | null {
  if (!puzzle || !puzzle.fen) {
    return null;
  }
  let game: Chess;
  try {
    game = new Chess(puzzle.fen);
  } catch {
    return null;
  }
  const plies = (puzzle.moves || '').split(/\s+/).filter(Boolean);
  if (plies[0] && plies[0].length >= 4) {
    try {
      game.move({
        from: plies[0].slice(0, 2),
        to: plies[0].slice(2, 4),
        promotion: 'q',
      });
    } catch {
      // keep raw fen
    }
  }
  const orientation = game.fen().split(' ')[1] === 'b' ? 'black' : 'white';
  return {fen: game.fen(), orientation};
}

export default function RoyaleSpectateView({
  coords,
  puzzles,
  followed,
  liveBoard,
  boardWidth,
}: Props) {
  const chessboardColorTheme = useChessboardColorTheme();
  const followedId = followed?.userId ?? null;

  // What the board currently shows (driven by the stream + settle logic).
  const [shown, setShown] = useState<Shown | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeqRef = useRef<number>(-1);

  // Reset when switching players: clear any pending settle + show their current
  // puzzle start until their stream catches up.
  useEffect(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    lastSeqRef.current = -1;
    if (followed && coords.length) {
      const id = coords[followed.myIndex % coords.length];
      setShown(currentPuzzleBoard(id ? puzzles[id] : undefined));
    } else {
      setShown(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedId]);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  // Apply each new streamed ply.
  useEffect(() => {
    if (!liveBoard || !liveBoard.fen) {
      return;
    }
    if (liveBoard.seq === lastSeqRef.current) {
      return; // already applied
    }
    lastSeqRef.current = liveBoard.seq;

    const next: Shown = {fen: liveBoard.fen, orientation: liveBoard.orientation};

    if (liveBoard.kind === 'setup') {
      // A new puzzle loaded: HOLD the prior (solved) position for SETTLE_MS,
      // then swap to the new puzzle's setup. (#3)
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = setTimeout(() => {
        setShown(next);
        settleTimerRef.current = null;
      }, SETTLE_MS);
      return;
    }

    // A player ply or auto-reply: apply immediately (animated by the board).
    if (settleTimerRef.current) {
      // A pending settle is interrupted by a real move — apply now.
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    setShown(next);
  }, [liveBoard]);

  if (!followed) {
    return (
      <View style={styles.emptyWrap}>
        <View
          style={{width: boardWidth, alignSelf: 'center'}}
          pointerEvents="none">
          <ChessboardWithArrow
            position={STARTING_FEN}
            boardOrientation="white"
            colorTheme={chessboardColorTheme}
            arePiecesDraggable={false}
            animationDuration={0}
          />
        </View>
        <CCText style={styles.emptyText}>
          Pick a player to follow their board.
        </CCText>
      </View>
    );
  }

  const fen = shown?.fen || STARTING_FEN;
  const orientation = shown?.orientation || 'white';

  return (
    <View style={styles.root}>
      <View
        style={{width: boardWidth, alignSelf: 'center'}}
        pointerEvents="none">
        <ChessboardWithArrow
          position={fen}
          boardOrientation={orientation}
          colorTheme={chessboardColorTheme}
          arePiecesDraggable={false}
          // Smooth slide between streamed plies.
          animationDuration={300}
        />
      </View>
      <CCText style={styles.followHint}>
        Following {followed.name || 'them'} live — their moves appear here.
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {gap: 12, alignItems: 'center'},
  emptyWrap: {gap: 12, alignItems: 'center'},
  emptyText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '700',
    color: TEAL_DARK,
    textAlign: 'center',
  },
  followHint: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MUTED,
    textAlign: 'center',
  },
});
